import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { QueryIntent } from '../constants';

type ErrorCorrelationContext = {
	searchSessionId?: string;
	queryHash?: string;
	sourceProduct?: string;
	isReadingAids?: boolean;
	queryIntent?: QueryIntent;
};

const getCorrelationAttributes = (context?: ErrorCorrelationContext) => {
	if (!context || !fg('kd_granular_network_errors')) {
		return {};
	}
	return {
		...(context.searchSessionId && { searchSessionId: context.searchSessionId }),
		...(context.queryHash && { queryHash: context.queryHash }),
		...(context.sourceProduct && { sourceProduct: context.sourceProduct }),
		...(context.isReadingAids !== undefined && { isReadingAids: context.isReadingAids }),
		...(context.queryIntent && { queryIntent: context.queryIntent }),
	};
};

export const getStreamFetchErrorEventPayload = (
	error: unknown,
	source: string,
	correlationContext?: ErrorCorrelationContext,
): AnalyticsEventPayload => {
	const correlationAttrs = getCorrelationAttributes(correlationContext);
	const captureOriginalError = fg('kd_sain_stream_error_diagnostics');

	if (error instanceof Error) {
		const reportedError =
			captureOriginalError && 'cause' in error && error.cause instanceof Error
				? error.cause
				: error;
		return {
			action: 'failed',
			actionSubject: 'aiAnswerDialogStreamFetch',
			source,
			eventType: 'track',
			attributes: {
				errorName: reportedError.name,
				errorMessage: reportedError.message,
				errorStack: captureOriginalError
					? reportedError.stack || ''
					: error.stack?.split('\n').at(1) || '',
				...('details' in error && { details: error.details }),
				...('errorCode' in error && { errorCode: error.errorCode }),
				// Surface the backend-derived fields (from StreamFailedError) so the
				// analytics event records what the backend actually returned rather
				// than only FE-synthesized values.
				...('reason' in error && { reason: error.reason }),
				...('backendFailureType' in error && { backendFailureType: error.backendFailureType }),
				...(fg('kd_sain_stream_error_diagnostics') && {
					...('diagnosticType' in error && { diagnosticType: error.diagnosticType }),
					...('failureStage' in error && { failureStage: error.failureStage }),
					...('responseContentType' in error && {
						responseContentType: error.responseContentType,
					}),
				}),
				...('traceId' in error && error.traceId ? { traceId: error.traceId } : {}),
				...correlationAttrs,
			},
		};
	}
	if (error instanceof Object && 'error' in error) {
		return {
			action: 'failed',
			actionSubject: 'aiAnswerDialogStreamFetch',
			source,
			eventType: 'track',
			attributes: {
				errorName: error.error instanceof Error ? error.error.name : '',
				errorMessage:
					error.error instanceof Error
						? error.error.message
						: typeof error === 'string'
							? error
							: '',
				errorStack:
					error.error instanceof Error
						? captureOriginalError
							? error.error.stack || ''
							: error.error.stack?.split('\n').at(1)
						: '',
				reason: 'reason' in error ? error.reason : '',
				details: 'details' in error ? error.details : '',
				...(fg('kd_sain_stream_error_diagnostics') && {
					...('diagnosticType' in error && { diagnosticType: error.diagnosticType }),
					...('failureStage' in error && { failureStage: error.failureStage }),
					...('responseContentType' in error && {
						responseContentType: error.responseContentType,
					}),
					...('traceId' in error && error.traceId ? { traceId: error.traceId } : {}),
				}),
				...correlationAttrs,
			},
		};
	}

	return {
		action: 'failed',
		actionSubject: 'aiAnswerDialogStreamFetch',
		source,
		eventType: 'track',
		attributes: {
			reason: error instanceof Object && 'reason' in error ? error.reason : '',
			details: error instanceof Object && 'details' in error ? error.details : '',
			...correlationAttrs,
		},
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getNonStreamFetchErrorEventPayload = (
	error: unknown,
	source: string,
	correlationContext?: ErrorCorrelationContext,
): AnalyticsEventPayload => {
	const correlationAttrs = getCorrelationAttributes(correlationContext);

	return {
		action: 'failed',
		actionSubject: 'aiAnswerDialogNonStreamFetch',
		source,
		eventType: 'track',
		attributes: {
			errorName: error instanceof Object && 'name' in error ? error.name : '',
			errorMessage: error instanceof Object && 'message' in error ? error.message : '',
			errorStack:
				error instanceof Object && 'stack' in error && typeof error.stack === 'string'
					? error.stack?.split('\n').at(1)
					: '',
			...correlationAttrs,
		},
	};
};
